<template>
  <div class="container mx-auto px-4 py-8" style="width: 90%;">
    <!-- 页面标题和门店信息 -->
    <div class="mb-8">
      <h2 class="text-3xl font-bold mb-2 ">投前风险评估报告</h2>
      <p class="text-gray-600">门店编号: {{ storeEntCode }}</p>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="text-center">
        <div class="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status"></div>
        <p class="mt-2 text-gray-600">数据加载中...</p>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
      <p>{{ error }}</p>
    </div>

    <!-- 主内容区 -->
    <div v-if="!loading && !error" class="space-y-10">   
      <!-- 在模板的公司基本信息部分修改 -->
      <section v-show="data && data.CompanyBasicInfo" class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">公司基本信息</h2>
        <!-- 新增固定宽度容器并设置自动换行 -->
        <div style="width: 90% !important; margin: 0 auto; overflow-x: auto; padding-bottom: 8px;">
          <table class="min-w-full divide-y divide-gray-200">
            <tbody class="bg-white divide-y divide-gray-200">
            <!-- 只显示有值的字段 -->
            <tr v-for="(item, index) in filteredCompanyBasicInfo" :key="index">
              <!-- 允许单元格内容自动换行 -->
              <td class="px-6 py-3 text-sm font-medium text-gray-900" style="white-space: normal;">{{ item.label }}</td>
              <td class="px-6 py-3 text-sm text-gray-500" style="white-space: normal;">{{ item.value }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>


      <!-- 2. 收入详情折线图 -->
      <section v-show="data && data.beforeInvestStoreIncomeDetailList" class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">收入详情趋势</h2>
        <div class="h-80 chart-wrapper">
          <div ref="incomeDetailChart" class="chart-container" id="incomeDetailChart"></div>
        </div>
      </section>

      <!-- 3. 修改利润表明细表格的容器样式 -->
      <section v-show="data && data.BeforeInvestIncomeStatementList" class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">利润表明细</h2>
        <div class="scrollable-table" style="width: 90% !important; margin: 0 auto; overflow-x: auto; padding-bottom: 8px;">
          <table class="divide-y divide-gray-200" style="min-width: 1200px; width: 100%;">
            <thead>
            <tr>
              <!-- 表头第一列固定且不透明 -->
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  style="position: sticky; left: 0; z-index: 10; min-width: 200px; background-color: #f9fafb;">
                字段
              </th>
              <th v-for="period in incomeStatementPeriods" :key="period" class="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {{ period }}
              </th>
              <th class="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最近12个月均值</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="field in incomeStatementFields" :key="field">
              <!-- 内容第一列固定且不透明 -->
              <td class="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap"
                  style="position: sticky; left: 0; z-index: 5; min-width: 200px; background-color: white; border-right: 1px solid #e5e7eb;">
                {{ getFieldComment('before_invest_income_statement', field) }}
              </td>
              <td v-for="period in incomeStatementPeriods" :key="period" class="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                {{ formatValue(getIncomeStatementValue(field, period)) }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{{ formatValue(getAvg12Value(field)) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 4. PCF收入详情折线图 -->
      <section v-show="data && data.beforeInvestStoreIncomeDetailPcfList" class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold">PCF收入详情趋势</h2>
          <button
            @click="forecastTurnover"
            :disabled="forecastLoading"
            class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {{ forecastLoading ? '预测中...' : '营业额预估' }}
          </button>
        </div>
        <div v-if="forecastMessage" class="mb-4 p-3 rounded" :class="forecastSuccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
          {{ forecastMessage }}
        </div>
        <div class="h-80 chart-wrapper">
          <div ref="pcfChart" class="chart-container" id="pcfChartContainer"></div>
        </div>

        <!-- 新增预估营业额总和显示 -->
        <div class="mt-4 p-4 bg-gray-50 rounded">
          <p class="text-lg font-medium">
            一年内预估营业额总和: {{ formatCurrency(calculatePcfTotal()) }}
          </p>
        </div>

        <!-- 新增：时间段营业额对比统计 -->
        <div class="mt-4 p-4 bg-blue-50 rounded border border-blue-100">
          <h3 class="text-base font-semibold text-blue-800" style="margin-top: 8px; margin-bottom: 12px;">营业额对比分析</h3>
          <p class="text-sm text-gray-700" style="margin-top: 8px; margin-bottom: 12px;">
            实际最近3个月累计营业额: {{ formatCurrency(last3MonthsTotal) }}
          </p>
          <p class="text-sm text-gray-700 mb-10">
            预估最近3个月累计营业额: {{ formatCurrency(pcfFirst3MonthsTotal) }}
          </p>
          <p class="text-sm text-gray-700" style="margin-top: 8px; margin-bottom: 12px;">
            预估 - 实际 : {{ formatCurrency(difference) }}
            <span :class="difference >= 0 ? 'text-green-600' : 'text-red-600'">
        ({{ difference >= 0 ? '+' : '' }}{{ (ratio * 100).toFixed(2) }}%)
      </span>
          </p>
        </div>
      </section>

      <!-- 5. 净利润趋势图 -->
      <section v-show="data && data.totalNet_profit && Object.keys(data.totalNet_profit).length > 0" class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">净利润趋势</h2>

        <div class="mb-4 flex items-center flex-wrap gap-4">
          <div class="flex items-center">
            <label class="mr-2 text-gray-700">股权占比:</label>
            <input
                type="number"
                v-model.number="equityRatio"
                min="0"
                max="100"
                class="border rounded px-2 py-1 w-24"
                @change="calculateEquityProfit"
            >
            <span class="mx-2">%</span>
            <button
                @click="calculateEquityProfit"
                class="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
            >
              计算
            </button>
          </div>

          <div class="flex items-center">
            <label class="mr-2 text-gray-700">PCF调整比例:</label>
            <input
                type="number"
                v-model.number="pcfAdjustmentRatio"
                class="border rounded px-2 py-1 w-24"
                step="0.01"
            >
            <span class="mx-2">%</span>
            <button
                @click="recalculateNetProfit"
                class="bg-green-500 text-white px-4 py-1 rounded hover:bg-green-600"
            >
              重新计算
            </button>
          </div>
        </div>

        <div class="h-80 chart-wrapper">
          <div ref="netProfitChart" class="chart-container" id="netProfitChartContainer"></div>
        </div>

        <div class="mt-4 p-4 bg-gray-50 rounded">
          <p class="text-lg font-medium">合计净利润 (按股权占比计算): {{ formatCurrency(totalEquityProfit) }}</p>
        </div>
      </section>

      <!-- 6. 投资方案测算与设计模块 -->
      <section class="bg-white rounded-lg shadow p-6 mt-6">
        <h2 class="text-xl font-semibold mb-6">投资方案测算与设计</h2>

        <!-- 输入参数区域 - 调整为每行显示两个输入项 -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" style="width: 90% !important; margin: 0 auto; padding-bottom: 8px;">
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">分成比例收入: </label>
            <input
                type="number"
                v-model="revenueSharingRatio"
                class="border border-gray-300 rounded-md px-3 py-2"
                min="0"
                max="100"
                step="0.01"
            >
            <span class="text-sm text-gray-500 mt-1">%</span>
          </div>

          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">敏感性测算比例: </label>
            <input
                type="number"
                v-model="sensitivityRatio"
                class="border border-gray-300 rounded-md px-3 py-2"
                min="0"
                max="100"
                step="0.01"
            >
            <span class="text-sm text-gray-500 mt-1">%</span>
          </div>

          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">预估投资金额: </label>
            <div class="flex items-center">
              <input
                  type="number"
                  v-model="investmentAmount"
                  class="border border-gray-300 rounded-md px-3 py-2 flex-1"
                  min="0"
                  step="1"
              >
              <span class="text-sm text-gray-500 ml-2">万</span>
            </div>
          </div>

          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">静态月度成本: </label>
            <input
                type="number"
                v-model="monthlyCostRatio"
                class="border border-gray-300 rounded-md px-3 py-2"
                min="0"
                max="100"
                step="0.01"
            >
            <span class="text-sm text-gray-500 mt-1">%</span>
          </div>
        </div>

        <!-- 敏感性分析测算表 -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                项目
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                基数
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                上涨1倍 ({{ sensitivityRatio }}%)
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                上涨2倍 ({{ sensitivityRatio * 2 }}%)
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                下降1倍 (-{{ sensitivityRatio }}%)
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                下降2倍 (-{{ sensitivityRatio * 2 }}%)
              </th>
            </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
            <!-- 预估营业额行 -->
            <tr>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">预估营业额</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ baseAmount.toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTurnover(1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTurnover(2).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTurnover(-1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTurnover(-2).toFixed(2) }}</td>
            </tr>

            <!-- 预估营业额/保本营业额行 -->
            <tr>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">预估营业额/保本营业额</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ (baseAmount / breakEvenAmount).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ (calculateTurnover(1) / breakEvenAmount).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ (calculateTurnover(2) / breakEvenAmount).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ (calculateTurnover(-1) / breakEvenAmount).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ (calculateTurnover(-2) / breakEvenAmount).toFixed(2) }}</td>
            </tr>

            <!-- 预估收入时长行 -->
            <tr>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">预估收入时长(月)</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateIncomeDuration().toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateIncomeDuration(1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateIncomeDuration(2).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateIncomeDuration(-1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateIncomeDuration(-2).toFixed(2) }}</td>
            </tr>

            <!-- 预估收益行 -->
            <tr>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">预估收益</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateProfit().toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateProfit(1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateProfit(2).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateProfit(-1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateProfit(-2).toFixed(2) }}</td>
            </tr>

            <!-- 总回款金额 -->
            <tr>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">总回款金额</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTotalRecovery().toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTotalRecovery(1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTotalRecovery(2).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTotalRecovery(-1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateTotalRecovery(-2).toFixed(2) }}</td>
            </tr>

            <!-- 融资方剩余利润 -->
            <tr>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">融资方剩余利润</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateRemainingProfit().toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateRemainingProfit(1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateRemainingProfit(2).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateRemainingProfit(-1).toFixed(2) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ calculateRemainingProfit(-2).toFixed(2) }}</td>
            </tr>

            </tbody>
          </table>
        </div>
      </section>

      <section v-if="data.totalNet_profit && Object.keys(data.totalNet_profit).length > 0" class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">实控人现金流</h2>
        <div class="mb-4">
          <span class="text-gray-700">按当前方案分成比例: </span>
          <span class="font-semibold">{{ revenueSharingRatio }}%</span>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">月份</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">营业额</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">净利润</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">分成比例</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">投资方分成</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">实控人现金流</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="row in controllerCashFlowRows" :key="row.label">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ row.label }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(row.turnover) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(row.net) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ (revenueSharingRatio || 0).toFixed(2) }}%</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(row.turnover * revenueSharingRatio / 100) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm" :class="row.ctrl < 0 ? 'text-red-600 font-semibold' : 'text-gray-900'">{{ formatCurrency(row.net -(row.turnover * revenueSharingRatio / 100)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, onBeforeUnmount, nextTick ,watch, getCurrentInstance} from 'vue';
import assetService from '@/services/assetService';
import * as echarts from 'echarts';

export default {
  props: {
    storeEntCode: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      // 输入参数默认值
      revenueSharingRatio: 10,      // 分成比例收入(%)
      equityRatio: 100,             // 股权占比(%)
      sensitivityRatio: 10,         // 敏感性测算比例(%)
      investmentAmount: 100,        // 预估投资金额(万)
      monthlyCostRatio: 1         // 静态月度成本(%)
    };
  },
  computed: {
    // 基数 = 一年内PCF的月度均值
    baseAmount() {
      const pcfList = this.data.beforeInvestStoreIncomeDetailPcfList;
      if (!pcfList || pcfList.length === 0) {
        return 0;
      }
      // 计算所有PCF数据的平均值
      const total = pcfList.reduce((sum, item) => {
        const value = Number(item.turnover || 0);
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
      return total / pcfList.length;
    },
    // 保本营业额
    breakEvenAmount() {
      return this.getGuaranteedRevenue();
    }
  },
  methods: {
    // 计算预估营业额
    calculateTurnover(multiple=0) {
      const ratio = 1 + (multiple * this.sensitivityRatio / 100);
      return this.baseAmount * ratio;
    },

    // 计算合计净利润（使用预估净利润）
    totalNetProfit(multiple=0) {
      const HuanKuanShiChang = multiple === 0 ? this.calculateIncomeDuration() : this.calculateIncomeDuration(multiple);
      return this.totalEquityProfit/12 * HuanKuanShiChang;
    },

    /**
     * 新的回款时长计算逻辑
     * 公式：回款时长 = （本金 + 本金 * 月息 * 使用时长（月份向上取整））/ 每月分成金额
     * 倒推计算回款时长
     */
    calculateIncomeDuration(multiple = 0) {
      // 获取当前营业额
      const turnover = multiple === 0 ? this.baseAmount : this.calculateTurnover(multiple);
      // 计算每月分成金额 = 预估营业额 * 分成比例
      const monthlyShareAmount = turnover * (this.revenueSharingRatio / 100);

      // 本金（转换为元）
      const principal = this.investmentAmount * 10000;

      // 月息（转换为小数）
      const monthlyInterest = this.monthlyCostRatio / 100;

      // 防止除数为零或负数（无法回款的情况）
      if (monthlyShareAmount <= principal * monthlyInterest) {
        return 0; // 或返回 Infinity 表示无法回款
      }

      // 倒推计算回款时长（解方程得出）
      const duration = principal / (monthlyShareAmount - principal * monthlyInterest);

      // 向上取整（使用时长按月份向上取整）
      return Math.ceil(duration);
    },

    // 计算预估收益
    calculateProfit(multiple = 0) {
      const duration = multiple === 0 ? this.calculateIncomeDuration() : this.calculateIncomeDuration(multiple);
      // console.log('回款时长', duration);
      const roundedUpDuration = Math.ceil(duration);
      // 向上取整的时长 * 静态月度成本比例 * 基数
      return roundedUpDuration * (this.monthlyCostRatio / 100) * this.investmentAmount*10000;
    },
    /**
     * 新增：计算总回款金额
     * 公式：总回款金额 = 本金 + 预估收益
     */
    calculateTotalRecovery(multiple = 0) {
      const principal = this.investmentAmount * 10000; // 本金（元）
      const profit = multiple === 0 ? this.calculateProfit() : this.calculateProfit(multiple);
      return principal + profit;
    },
  /**
   * 新增：计算融资方剩余利润
   * 公式：融资方剩余利润 = 合计净利润 - 总回款金额
   */
  calculateRemainingProfit(multiple = 0) {
    const netProfit = multiple === 0 ? this.totalNetProfit() : this.totalNetProfit(multiple);
    const totalRecovery = multiple === 0 ? this.calculateTotalRecovery() : this.calculateTotalRecovery(multiple);

    return netProfit - totalRecovery;
  }
  },
  setup(props) {
    // 状态管理
    const loading = ref(true);
    const error = ref(null);
    const data = ref({});
    const equityRatio = ref(100);
    const totalEquityProfit = ref(0);
    const pcfAdjustmentRatio = ref(0);
    
    // 营业额预估相关状态
    const forecastLoading = ref(false);
    const forecastMessage = ref('');
    const forecastSuccess = ref(false);

    // 图表容器引用 - 确保命名一致
    const incomeDetailChart = ref(null);
    const pcfChart = ref(null);
    const netProfitChart = ref(null);
    const cashFlowChart = ref(null);
    const last12Periods = computed(() => {
      const arr = Array.isArray(incomeStatementPeriods.value) ? [...incomeStatementPeriods.value] : [];
      return arr.slice(Math.max(0, arr.length - 12));
    });
    const { proxy } = getCurrentInstance();

    // ECharts实例
    let incomeChartInstance = null;
    let pcfChartInstance = null;
    let profitChartInstance = null;
    let cashChartInstance = null;

    //公司基本信息中，为空的数据不做展示
    const filteredCompanyBasicInfo = computed(() => {
      if (!data.value.CompanyBasicInfo) return [];

      // 需要排除的字段列表
      const excludedFields = ['id', 'keyNo', 'operId', 'province', 'updatedDate'];

      return Object.entries(data.value.CompanyBasicInfo)
          // 先过滤掉空值和特定字段
          .filter(([key, value]) =>
              !excludedFields.includes(key) &&  // 排除指定字段
              value && value !== '-' && value !== ''  // 排除空值
          )
          .map(([key, value]) => ({
            label: getFieldComment('company_basic_info', key),
            value: value
          }));
    });

    // 检查图表容器是否存在的增强版函数
    const checkChartContainer = (containerRef, containerId) => {
      if (!containerRef.value) {
        const idToFind = containerId;
        console.error(`图表容器${idToFind}未找到，尝试通过ID查找...`);
        const element = document.getElementById(idToFind);
        if (element) {
          containerRef.value = element;
          console.warn(`已通过ID找到容器: ${idToFind}`);
          return true;
        }
        console.error(`无法找到容器 ${idToFind}，请检查模板中的ref或id是否正确`);
        return false;
      }
      return true;
    };

    // 在script部分修改incomeStatementFields计算属性
    const incomeStatementFields = computed(() => {
      try {
        if (!data.value.BeforeInvestIncomeStatementList || data.value.BeforeInvestIncomeStatementList.length === 0) {
          return [];
        }
        // 定义需要显示的字段列表（按需要的顺序排列）
        const requiredFields = [
          'period', // 月份
          'operatingIncome', // 营业收入
          'operatingCost', // 营业成本
          'grossProfit', // 毛利润
          'fixedCosts', // 固定费用合计
          'storeRent', // 固定费用-店面租金
          'storePropertyFee', // 固定费用-店面物业费
          'semiFixedCosts', // 半固定费用合计
          'salary', // 半固定费用-工资
          'waterFee', // 半固定费用-水费
          'electricityFee', // 半固定费用-电费
          'variableCosts', // 变动费用合计
          'depreciationFee', // 变动费用-折旧费
          'netProfit', // 净利润
          'netProfitMargin' // 净利润率
        ];

        // 只返回需要的字段（过滤掉其他字段）
        return requiredFields;
      } catch (err) {
        console.error(`[incomeStatementFields 计算错误] ${err.message}`, { data: data.value });
        return []; // 出错时返回空数组
      }
    });

    const incomeStatementPeriods = computed(() => {
      try {
        if (!data.value.BeforeInvestIncomeStatementList) return [];
        const periods = data.value.BeforeInvestIncomeStatementList.map(item => item.period).sort();
        return periods;
      } catch (err) {
        console.error(`[incomeStatementPeriods 计算错误] ${err.message}`, { data: data.value });
        return []; // 出错时返回空数组
      }
    });

    // 检查图表容器尺寸是否有效
    const checkContainerSize = (container) => {
      if (!container) {
        console.warn('图表容器不存在');
        return false;
      }

      const { clientWidth, clientHeight } = container;
      if (clientWidth === 0 || clientHeight === 0) {
        console.warn(`图表容器尺寸无效: ${clientWidth}x${clientHeight}`);
        // 尝试强制设置最小尺寸
        container.style.minWidth = '400px';
        container.style.minHeight = '300px';
        // 重新检查
        return container.clientWidth > 0 && container.clientHeight > 0;
      }
      return true;
    };

    // 计算股权净利润
    const calculateEquityProfit = () => {
      try {
        if (!data.value.totalNet_profit) {
          console.warn('calculateEquityProfit: 没有找到净利润数据');
          return;
        }

        const total = Object.values(data.value.totalNet_profit).reduce((sum, val) => sum + val, 0);
        totalEquityProfit.value = total * (equityRatio.value / 100);

        // 更新图表
        renderNetProfitChart();
      } catch (err) {
        console.error(`[calculateEquityProfit 错误] ${err.message}`, {
          totalNetProfit: data.value.totalNet_profit,
          equityRatio: equityRatio.value
        });
      }
    };

    // 重新计算净利润（基于PCF调整比例）
    const recalculateNetProfit = async () => {
      try {
        const requestData = {
          store_ent_code: props.storeEntCode,
          pcf_adjustment_ratio: pcfAdjustmentRatio.value
        };

        // 同时请求PCF收入和净利润数据
        const [pcfIncomeRes, estimatedNetProfitRes] = await Promise.all([
          assetService.getPcfIncome(requestData),
          assetService.getEstimatedNetProfit(requestData)
        ]);

        // 更新PCF收入数据
        if (pcfIncomeRes && pcfIncomeRes.data) {
          data.value.beforeInvestStoreIncomeDetailPcfList = pcfIncomeRes.data || [];
          // 重新渲染PCF图表
          nextTick(() => {
            renderPcfChart();
          });
        }

        // 更新净利润数据
        if (estimatedNetProfitRes && estimatedNetProfitRes.data) {
          data.value.totalNet_profit = estimatedNetProfitRes.data || {};
          
          // 重新计算股权净利润
          calculateEquityProfit();
        }

        if (!pcfIncomeRes || !pcfIncomeRes.data || !estimatedNetProfitRes || !estimatedNetProfitRes.data) {
          alert('重新计算数据失败');
        }
      } catch (err) {
        console.error(`[recalculateNetProfit 错误] ${err.message}`);
        alert('重新计算数据失败: ' + err.message);
      }
    };

    // 监听数据变化，当数据加载完成且容器可用时渲染图表
    watch(
        () => data.value.beforeInvestStoreIncomeDetailList,
        (newVal) => {
          if (newVal) {
            nextTick(() => {
              renderIncomeDetailChart();
            });
          }
        }
    );

    // 1. 先提取公共配置，避免重复并方便调试
    const getGuaranteedRevenueMarkLine = (data) => {
      // 调试：打印数据确认是否存在有效值
      // console.log('保本营业额数据:', data.value?.guaranteed_revenue);

      // 更严谨的条件判断（允许0值，排除null/undefined）
      const hasValue = data.value?.guaranteed_revenue !== undefined
          && data.value?.guaranteed_revenue !== null
          && data.value?.guaranteed_revenue !== '';

      if (!hasValue) {
        console.log('无有效保本营业额数据');
        return null;
      }

      // 确保数值转换正确
      const revenueValue = Number(data.value.guaranteed_revenue);
      if (isNaN(revenueValue)) {
        console.error('保本营业额无法转换为数字:', data.value.guaranteed_revenue);
        return null;
      }

      return {
        symbol: ['none', 'none'],
        data: [{
          name: getFieldComment('before_invest_income_statement', 'guaranteed_revenue'),
          yAxis: revenueValue, // 使用转换后的值
          lineStyle: {
            color: '#ff4d4f', // 红色，确保与背景有反差
            width: 3, // 加宽线条，更容易看到
            type: 'dashed'
          },
          label: {
            show: true,
            formatter: `${getFieldComment('before_invest_income_statement', 'guaranteed_revenue')}: ¥{c}`,
            position: 'end',
            color: '#333' // 确保标签文字可见
          }
        }]
      };
    };

    // 2. 渲染收入详情图表 - 包含营业额趋势线
    const renderIncomeDetailChart = () => {
      try {
        // 首先检查数据是否存在
        if (!data.value.beforeInvestStoreIncomeDetailList) {
          console.warn('renderIncomeDetailChart: 没有收入详情数据，无法渲染图表');
          return;
        }

        // 检查容器是否存在
        if (!checkChartContainer(incomeDetailChart, 'incomeDetailChart')) {
          let attempts = 0;
          const timer = setInterval(() => {
            attempts++;
            if (checkChartContainer(incomeDetailChart, 'incomeDetailChart')) {
              clearInterval(timer);
              renderIncomeDetailChart();
            } else if (attempts >= 10) {
              clearInterval(timer);
              console.error('incomeDetailChart 容器持续不可用，放弃渲染');
            }
          }, 300);
          return;
        }

        // 检查容器尺寸
        if (!checkContainerSize(incomeDetailChart.value)) {
          console.error('renderIncomeDetailChart: 容器尺寸无效，无法渲染图表');
          // 500ms后重试
          setTimeout(renderIncomeDetailChart, 500);
          return;
        }

        // 销毁已有实例
        if (incomeChartInstance) {
          incomeChartInstance.dispose();
        }

        // 初始化图表
        incomeChartInstance = echarts.init(incomeDetailChart.value);

        // 准备数据并排序 - 不做任何时间范围过滤，使用全部数据
        const sortedData = [...data.value.beforeInvestStoreIncomeDetailList]
            .sort((a, b) => new Date(a.incomeDate) - new Date(b.incomeDate));

        // 提取日期和营业额数据 - 使用全部数据点
        const labels = sortedData.map(item => item.incomeDate);
        const operatingIncome = sortedData.map(item => {
          const value = Number(item.turnover || 0);
          return isNaN(value) ? 0 : value;
        });

        // 计算营业额7天移动平均线（趋势线）
        const calculateMovingAverage = (data, windowSize) => {
          const result = [];
          for (let i = 0; i < data.length; i++) {
            if (i < windowSize - 1) {
              // 数据不足窗口大小时，使用已有数据的平均值
              const slice = data.slice(0, i + 1);
              const avg = slice.reduce((sum, val) => sum + val, 0) / slice.length;
              result.push(avg);
            } else {
              // 计算窗口内的平均值
              const slice = data.slice(i - windowSize + 1, i + 1);
              const avg = slice.reduce((sum, val) => sum + val, 0) / windowSize;
              result.push(avg);
            }
          }
          return result;
        };

        // 计算7天移动平均作为趋势线
        const trendLine = calculateMovingAverage(operatingIncome, 90);

        // 根据数据量动态调整X轴标签显示策略
        const labelInterval = Math.max(1, Math.floor(labels.length / 30)); // 最多显示30个标签，避免拥挤

        const markLine = getGuaranteedRevenueMarkLine(data);

        // 图表配置
        const option = {
          title: {
            text: `收入详情趋势\n\n ${getGuaranteedRevenueText(data)}`,
            left: 'center',
            subtextStyle: {
              color: getRandomColor()
            }
          },
          tooltip: {
            trigger: 'axis',
            formatter: (params) => {
              let result = `<div style="font-weight: bold">${params[0].name}</div>`;
              params.forEach(item => {
                result += `<div>${item.seriesName}: ¥${item.value.toLocaleString()}</div>`;
              });
              return result;
            }
          },
          legend: {
            data: [
              getFieldComment('before_invest_income_statement', 'operatingIncome'),
              '营业额趋势线(90天平均)'
            ],
            bottom: 10
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '15%',
            top: '15%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: labels,
            axisLabel: {
              rotate: 45,
              interval: labelInterval, // 根据数据量动态调整显示间隔
              showMinLabel: true,     // 始终显示第一个标签
              showMaxLabel: true      // 始终显示最后一个标签
            }
          },
          yAxis: {
            type: 'value',
            name: '金额 (¥)',
            nameLocation: 'end',
            axisLabel: {
              formatter: (value) => `${value.toLocaleString()}`
            }
          },
          dataZoom: [
            {
              type: 'slider',
              show: true,
              xAxisIndex: 0,
              start: 0,
              end: 100
            },
            {
              type: 'inside',
              xAxisIndex: 0,
              start: 0,
              end: 100
            }
          ],
          series: [
            {
              name: getFieldComment('before_invest_income_statement', 'operatingIncome'),
              type: 'line',
              data: operatingIncome,
              lineStyle: {
                color: '#3e95cd',
                width: 2
              },
              areaStyle: {
                color: 'rgba(62, 149, 205, 0.1)'
              },
              symbol: 'circle',
              symbolSize: 4,
              smooth: true,
              yAxisIndex: 0,
              ...(markLine ? { markLine } : {})
            },
            // 营业额趋势线（7天移动平均）
            {
              name: '营业额趋势线(90天平均)',
              type: 'line',
              data: trendLine,
              lineStyle: {
                color: '#ffce56',
                width: 3
              },
              symbol: 'none',
              smooth: true,
              yAxisIndex: 0,
              z: 10  // 确保趋势线显示在最上层
            },

          ],
          // 添加保本营业额参考线
          markLine: {
            symbol: ['none', 'none'],
            data: data.value.guaranteed_revenue ? [{
              name: getFieldComment('before_invest_income_statement', 'guaranteed_revenue'),
              yAxis: Number(data.value.guaranteed_revenue),
              lineStyle: {
                color: '#ff4d4f',
                width: 2,
                type: 'dashed'
              },
              label: {
                show: true,
                formatter: `${getFieldComment('before_invest_income_statement', 'guaranteed_revenue')}: ¥{c}`,
                position: 'end'
              }
            }] : []
          }
        };

        incomeChartInstance.setOption(option);
        window.addEventListener('resize', () => incomeChartInstance?.resize());

      } catch (err) {
        console.error(`[renderIncomeDetailChart 错误] ${err.message}`, {
          data: data.value.beforeInvestStoreIncomeDetailList
        });
      }
    };

    // 3. 渲染PCF收入详情图表
    const renderPcfChart = () => {
      try {
        if (!data.value.beforeInvestStoreIncomeDetailPcfList) {
          console.warn('renderPcfChart: 没有PCF收入详情数据，无法渲染图表');
          return;
        }

        if (!checkChartContainer(pcfChart, 'pcfChartContainer')) {
          let attempts = 0;
          const timer = setInterval(() => {
            attempts++;
            if (checkChartContainer(pcfChart, 'pcfChartContainer')) {
              clearInterval(timer);
              renderPcfChart();
            } else if (attempts >= 10) {
              clearInterval(timer);
              console.error('pcfChartContainer 容器持续不可用，放弃渲染');
            }
          }, 300);
          return;
        }

        // 销毁已有实例
        if (pcfChartInstance) {
          pcfChartInstance.dispose();
        }

        // 初始化图表
        pcfChartInstance = echarts.init(pcfChart.value);

        // 准备PCF数据并排序（月级别数据）
        const pcfData = [...data.value.beforeInvestStoreIncomeDetailPcfList]
            .sort((a, b) => new Date(a.incomeDate) - new Date(b.incomeDate));

        // 准备实际营业额数据并排序（月级别数据）
        const incomeData = data.value.beforeInvestStoreIncomeDetailList ? 
            [...data.value.beforeInvestStoreIncomeDetailList]
                .sort((a, b) => new Date(a.incomeDate) - new Date(b.incomeDate)) : [];

        // 获取所有唯一月份并排序
        const allMonthsSet = new Set();
        incomeData.forEach(item => allMonthsSet.add(item.incomeDate));
        pcfData.forEach(item => allMonthsSet.add(item.incomeDate));
        const allMonths = Array.from(allMonthsSet).sort((a, b) => new Date(a) - new Date(b));

        // 为每个系列创建数据数组，没有数据的月份使用null
        const incomeValues = allMonths.map(month => {
          const item = incomeData.find(d => d.incomeDate === month);
          return item ? item.turnover || 0 : null;
        });

        const pcfValues = allMonths.map(month => {
          const item = pcfData.find(d => d.incomeDate === month);
          return item ? item.turnover || 0 : null;
        });

        // 1. 找到PCF有数据的第一个月份的索引
        const pcfStartIndex = allMonths.findIndex(month => {
          return pcfData.some(item => item.incomeDate === month);
        });

        // 2. 找到实际营业额有数据的最后一个月份的索引，用于计算偏差结束时间（倒数第二个月）
        let incomeEndIndex = -1;
        for (let i = incomeValues.length - 1; i >= 0; i--) {
          if (incomeValues[i] !== null) {
            incomeEndIndex = i;
            break;
          }
        }
        const deviationEndIndex = Math.max(pcfStartIndex, incomeEndIndex - 1);

        // 3. 计算近三个月移动平均值
        const movingAvg3Month = [];
        for (let i = 0; i < allMonths.length; i++) {
          if (i < pcfStartIndex) {
            movingAvg3Month.push(null);
            continue;
          }
          const startIndex = Math.max(pcfStartIndex, i - 2);
          const slice = pcfValues.slice(startIndex, i + 1);
          const validValues = slice.filter(val => val !== null && !isNaN(val));
          movingAvg3Month.push(validValues.length > 0 ? 
            validValues.reduce((sum, val) => sum + val, 0) / validValues.length : null);
        }

        // 4. 计算累计PCF与实际营业额的偏差
        const cumulativePcf = [];
        const cumulativeIncome = [];
        let pcfSum = 0, incomeSum = 0;
        
        allMonths.forEach((month, index) => {
          if (index >= pcfStartIndex) {
            pcfSum += pcfValues[index] || 0;
            incomeSum += incomeValues[index] || 0;
          }
          cumulativePcf.push(pcfSum);
          cumulativeIncome.push(incomeSum);
        });

        // 计算累计偏差，只在指定时间范围内显示
        const cumulativeDeviation = cumulativePcf.map((pcf, index) => {
          if (index >= pcfStartIndex && index <= deviationEndIndex) {
            return pcf - cumulativeIncome[index];
          }
          return null;
        });

        // 5. 从PCF有数据的月份开始过滤数据
        const filteredMonths = allMonths.slice(pcfStartIndex);
        const filteredIncomeValues = incomeValues.slice(pcfStartIndex);
        const filteredPcfValues = pcfValues.slice(pcfStartIndex);
        const filteredMovingAvg3Month = movingAvg3Month.slice(pcfStartIndex);
        const filteredCumulativeDeviation = cumulativeDeviation.slice(pcfStartIndex);

        const markLine = getGuaranteedRevenueMarkLine(data);

        // 设置图表配置
        const option = {
          title: {
            text: 'PCF收入详情趋势',
            left: 'center'
          },
          tooltip: {
            trigger: 'axis',
            formatter: (params) => {
              const currentMonth = params[0]?.name || '未知月份';
              
              // 查找各系列数据
              const incomeItem = params.find(p => p.seriesName === '实际营业额');
              const pcfItem = params.find(p => p.seriesName === 'PCF收入');
              const avgItem = params.find(p => p.seriesName === '近三月平均');
              const deviationItem = params.find(p => p.seriesName === '累计偏差');
              
              // 格式化数值，处理null和undefined
              const formatValue = (value) => {
                if (value === null || value === undefined) return '无数据';
                return `¥${value.toLocaleString()}`;
              };
              
              const formatFloatValue = (value) => {
                if (value === null || value === undefined) return '无数据';
                return `¥${value.toFixed(2).toLocaleString()}`;
              };
              
              return `
                <div style="font-weight: bold">${currentMonth}</div>
                <div>实际营业额: ${formatValue(incomeItem?.value)}</div>
                <div>PCF收入: ${formatValue(pcfItem?.value)}</div>
                ${avgItem?.value !== null && avgItem?.value !== undefined ? `<div>近三月平均预估营业额: ${formatFloatValue(avgItem.value)}</div>` : ''}
                ${deviationItem?.value !== null && deviationItem?.value !== undefined ? `<div>累计偏差: ${formatValue(deviationItem.value)}</div>` : ''}
              `;
            }
          },
          legend: {
            data: ['实际营业额', 'PCF收入', '近三月平均', '累计偏差'],
            bottom: 10
          },
          grid: {
            left: '3%',
            right: '10%', // 为右侧Y轴预留空间
            bottom: '15%',
            top: '15%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: filteredMonths,
            axisLabel: {
              rotate: 45,
              interval: 'auto'
            },
            splitLine: {
              show: false
            }
          },
          yAxis: [
            {
              type: 'value',
              name: '金额 (¥)',
              nameLocation: 'end',
              axisLabel: {
                formatter: (value) => `${value.toLocaleString()}`
              },
              splitLine: {
                show: false
              }
            },
            {
              type: 'value',
              name: '累计偏差 (¥)',
              nameLocation: 'end',
              axisLabel: {
                formatter: (value) => `${value.toLocaleString()}`
              },
              position: 'right',
              splitLine: {
                show: false
              }
            }
          ],
          series: [
            // 实际营业额系列
            {
              name: '实际营业额',
              type: 'line',
              data: filteredIncomeValues,
              lineStyle: {
                color: '#36a2eb',
                width: 2
              },
              areaStyle: {
                color: 'rgba(54, 162, 235, 0.1)'
              },
              symbol: 'circle',
              symbolSize: 4,
              smooth: true,
              connectNulls: false,
              showSymbol: false,
              emphasis: {
                showSymbol: true
              },
              yAxisIndex: 0
            },
            // PCF收入数据系列
            {
              name: 'PCF收入',
              type: 'line',
              data: filteredPcfValues,
              lineStyle: {
                color: '#4bc0c0',
                width: 2
              },
              areaStyle: {
                color: 'rgba(75, 192, 192, 0.1)'
              },
              symbol: 'circle',
              symbolSize: 4,
              smooth: true,
              connectNulls: false,
              showSymbol: false,
              emphasis: {
                showSymbol: true
              },
              yAxisIndex: 0,
              ...(markLine ? { markLine } : {})
            },
            // 近三月移动平均线
            {
              name: '近三月平均',
              type: 'line',
              data: filteredMovingAvg3Month,
              lineStyle: {
                color: '#ff9f40',
                width: 2,
                type: 'dashed'
              },
              symbol: 'circle',
              symbolSize: 4,
              smooth: true,
              connectNulls: true,
              showSymbol: false,
              emphasis: {
                showSymbol: true
              },
              yAxisIndex: 0
            },
            // 累计偏差系列
            {
              name: '累计偏差',
              type: 'line',
              data: filteredCumulativeDeviation,
              lineStyle: {
                color: '#ff6b6b',
                width: 2
              },
              symbol: 'circle',
              symbolSize: 4,
              smooth: true,
              connectNulls: false,
              showSymbol: false,
              emphasis: {
                showSymbol: true
              },
              yAxisIndex: 1
            }
          ]
        };

        pcfChartInstance.setOption(option);
        window.addEventListener('resize', () => pcfChartInstance?.resize());
      } catch (err) {
        console.error(`[renderPcfChart 错误] ${err.message}`, {
          data: data.value.beforeInvestStoreIncomeDetailPcfList
        });
      }
    };

    // 4. 渲染净利润图表
    const renderNetProfitChart = () => {
      try {
        // 检查数据是否存在且有效
        if (!data.value.totalNet_profit || Object.keys(data.value.totalNet_profit).length === 0) {
          console.warn('renderNetProfitChart: 没有有效的净利润数据，无法渲染图表');
          return;
        }

        // 检查并获取图表容器
        if (!checkChartContainer(netProfitChart, 'netProfitChartContainer')) {
          // 500ms后重试
          //setTimeout(renderNetProfitChart, 500);
          return;
        }

        // 检查容器尺寸
        if (!checkContainerSize(netProfitChart.value)) {
          console.error('renderNetProfitChart: 容器尺寸无效，无法渲染图表');
          // 500ms后重试
          // setTimeout(renderNetProfitChart, 500);
          return;
        }

        // 销毁已有实例
        if (profitChartInstance) {
          profitChartInstance.dispose();
        }

        // 初始化图表
        profitChartInstance = echarts.init(netProfitChart.value);

        // 准备数据并按月排序
        const entries = Object.entries(data.value.totalNet_profit);

        // 转换日期格式并排序（确保按月正确排序）
        const sortedEntries = entries.sort((a, b) => {
          // 解析日期字符串为月份
          const dateA = new Date(a[0]);
          const dateB = new Date(b[0]);
          // 比较年月（忽略日）
          return dateA.getFullYear() - dateB.getFullYear() || dateA.getMonth() - dateB.getMonth();
        });

        // 格式化标签为"YYYY-MM"格式
        const labels = sortedEntries.map(entry => {
          const date = new Date(entry[0]);
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        });

        const originalProfit = sortedEntries.map(entry => {
          const value = Number(entry[1]);
          return isNaN(value) ? 0 : value;
        });

        const equityProfit = originalProfit.map(val => val * (equityRatio.value / 100));

        // 准备PCF营业额数据
        const pcfTurnoverData = data.value.beforeInvestStoreIncomeDetailPcfList ? 
            [...data.value.beforeInvestStoreIncomeDetailPcfList]
                .sort((a, b) => new Date(a.incomeDate) - new Date(b.incomeDate)) : [];

        // 将PCF数据映射到对应的月份标签
        const pcfTurnoverValues = labels.map(label => {
          const item = pcfTurnoverData.find(d => {
            const date = new Date(d.incomeDate);
            const monthLabel = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            return monthLabel === label;
          });
          return item ? Number(item.turnover || 0) : null;
        });

        // 设置图表配置
        const option = {
          title: {
            text: '净利润趋势（按月）',
            left: 'center'
          },
          tooltip: {
            trigger: 'axis',
            formatter: (params) => {
              let result = `<div style="font-weight: bold">${params[0].name}</div>`;
              params.forEach(param => {
                if (param && param.value !== null && param.value !== undefined) {
                  const value = Number(param.value);
                  if (!isNaN(value)) {
                    result += `<div>${param.seriesName}: ¥${value.toLocaleString()}</div>`;
                  }
                }
              });
              return result;
            }
          },
          legend: {
            data: [
              getFieldComment('before_invest_income_statement', 'netProfit'),
              `按${equityRatio.value}%股权计算的${getFieldComment('before_invest_income_statement', 'netProfit')}`,
              'PCF预估营业额'
            ],
            bottom: 10
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '15%',
            top: '15%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: labels,
            axisLabel: {
              rotate: 45,
              // 动态调整标签间隔，避免拥挤
              interval: Math.max(0, Math.floor(labels.length / 12)) // 最多显示12个标签
            }
          },
          yAxis: {
            type: 'value',
            name: '金额 (¥)',
            nameLocation: 'end',
            axisLabel: {
              formatter: (value) => `${value.toLocaleString()}`
            }
          },
          // 增加数据缩放功能，方便查看大量数据
          dataZoom: [
            {
              type: 'slider',
              show: true,
              xAxisIndex: 0,
              start: 0,
              end: 100
            },
            {
              type: 'inside',
              xAxisIndex: 0
            }
          ],
          series: [
            {
              name: getFieldComment('before_invest_income_statement', 'netProfit'),
              type: 'line',
              data: originalProfit,
              lineStyle: {
                color: '#9966ff',
                width: 2
              },
              areaStyle: {
                color: 'rgba(153, 102, 255, 0.1)'
              },
              symbol: 'circle',
              symbolSize: 4,
              smooth: true
            },
            {
              name: `按${equityRatio.value}%股权计算的${getFieldComment('before_invest_income_statement', 'netProfit')}`,
              type: 'line',
              data: equityProfit,
              lineStyle: {
                color: '#ffce56',
                width: 2,
                type: 'dashed'
              },
              symbol: 'circle',
              symbolSize: 4,
              smooth: true
            },
            {
              name: 'PCF预估营业额',
              type: 'line',
              data: pcfTurnoverValues,
              lineStyle: {
                color: '#36a2eb',
                width: 2,
                type: 'dotted'
              },
              symbol: 'triangle',
              symbolSize: 4,
              smooth: true
            }
          ]
        };

        profitChartInstance.setOption(option);
        window.addEventListener('resize', () => profitChartInstance?.resize());

      } catch (err) {
        console.error(`[renderNetProfitChart 错误] ${err.message}`, {
          totalNetProfit: data.value.totalNet_profit,
          equityRatio: equityRatio.value
        });
      }
    };

    // 5. 计算 pcf 预估营业额总和
    const calculatePcfTotal = () => {
      try {
        if (!data.value.beforeInvestStoreIncomeDetailPcfList || data.value.beforeInvestStoreIncomeDetailPcfList.length === 0) {
          return 0;
        }
        // 计算所有预估营业额的总和
        return data.value.beforeInvestStoreIncomeDetailPcfList.reduce((sum, item) => {
          const value = Number(item.turnover || 0);
          return sum + (isNaN(value) ? 0 : value);
        }, 0);
      } catch (err) {
        console.error(`[calculatePcfTotal 错误] ${err.message}`);
        return 0;
      }
    };

    // 计算实际最近3个月累计营业额
    const last3MonthsTotal = computed(() => {
      if (!data.value.beforeInvestStoreIncomeDetailList) return 0;

      return data.value.beforeInvestStoreIncomeDetailList
          .slice(-3) // 取最后3个月
          .reduce((sum, item) => sum + (Number(item.turnover) || 0), 0);
    });

    // 计算PCF前3个月累计营业额
    const pcfFirst3MonthsTotal = computed(() => {
      if (!data.value.beforeInvestStoreIncomeDetailPcfList) return 0;

      return data.value.beforeInvestStoreIncomeDetailPcfList
          .slice(0, 3) // 取前3个月
          .reduce((sum, item) => sum + (Number(item.turnover) || 0), 0);
    });

    // 计算差额
    const difference = computed(() => {
      return pcfFirst3MonthsTotal.value - last3MonthsTotal.value;
    });

// 计算比例
    const ratio = computed(() => {
      if (last3MonthsTotal.value === 0) return 0;
      return difference.value / last3MonthsTotal.value;
    });

    const getGuaranteedRevenue = () =>{
      try {
        // 确保数据结构存在
        if (!data.value || data.value.guaranteed_revenue === undefined || data.value.guaranteed_revenue === null) {
          return 0;
        }
        
        // 转换为数字并验证有效性
        const revenue = Number(data.value.guaranteed_revenue);
        return isNaN(revenue) ? 0 : revenue;
      } catch (error) {
        console.error('[getGuaranteedRevenue 错误]', error);
        return 0;
      }
    };
    // 生成随机颜色
    const getRandomColor = () => {
      try {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
          color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
      } catch (err) {
        console.error(`[getRandomColor 错误] ${err.message}`);
        return '#000000'; // 出错时返回默认黑色
      }
    };

    // 获取数据
    const fetchData = async () => {
      try {
        const code = props.storeEntCode;
        if (!code) {
          const errMsg = '未获取到门店编码参数';
          error.value = errMsg;
          console.error(`[fetchData 错误] ${errMsg}`);
          loading.value = false;
          return;
        }
        // 请求数据
        const requestData = {
          store_ent_code: code
        };
        
        // 并行调用所有API
        const [companyBasicInfoRes, incomeStatementRes, historicalIncomeRes, pcfIncomeRes, estimatedNetProfitRes] = await Promise.all([
          assetService.getCompanyBasicInfo(requestData),
          assetService.getIncomeStatement(requestData),
          assetService.getHistoricalIncome(requestData),
          assetService.getPcfIncome(requestData),
          assetService.getEstimatedNetProfit(requestData)
        ]);
        
        // 初始化数据结构
        data.value = {
          CompanyBasicInfo: null,
          beforeInvestStoreIncomeDetailList: [],
          beforeInvestStoreIncomeDetailPcfList: [],
          BeforeInvestIncomeStatementList: [],
          totalNet_profit: {},
          fixed_costs_avg: 0,
          guaranteed_revenue: 0
        };
        
        // 处理公司基本信息
        if (companyBasicInfoRes && companyBasicInfoRes.data) {
          data.value.CompanyBasicInfo = companyBasicInfoRes.data;
        }
        
        // 处理历史财务报表
        if (incomeStatementRes && incomeStatementRes.data) {
          data.value.BeforeInvestIncomeStatementList = incomeStatementRes.data.BeforeInvestIncomeStatementList || [];
          data.value.fixed_costs_avg = incomeStatementRes.data.fixed_costs_avg || 0;
          data.value.guaranteed_revenue = incomeStatementRes.data.guaranteed_revenue || 0;
        }
        
        // 处理历史收入详情
        if (historicalIncomeRes && historicalIncomeRes.data) {
          data.value.beforeInvestStoreIncomeDetailList = historicalIncomeRes.data || [];
        }
        
        // 处理PCF预估收入
        if (pcfIncomeRes && pcfIncomeRes.data) {
          data.value.beforeInvestStoreIncomeDetailPcfList = pcfIncomeRes.data || [];
        }
        
        // 处理预估净利润
        if (estimatedNetProfitRes && estimatedNetProfitRes.data) {
          data.value.totalNet_profit = estimatedNetProfitRes.data || {};
        }
        
        console.log('所有API返回数据:', data.value);

        // 确保DOM更新后再渲染图表
        nextTick(() => {
          renderIncomeDetailChart();
          renderPcfChart();
          // 初始化合计净利润
          calculateEquityProfit();
          renderNetProfitChart();
          renderCashFlowChart();
        });

      } catch (err) {
        const errMsg = `数据获取失败: ${err.message}`;
        error.value = errMsg;
        console.error(`[fetchData 错误] ${errMsg}`, {
          storeEntCode: props.storeEntCode,
          error: err
        });
      } finally {
        loading.value = false;
      }
    };

    // 在组件挂载时检查容器
    onMounted(() => {
      try {
        console.log('组件挂载，开始获取数据');
        // 使用nextTick确保DOM已准备就绪
        nextTick(() => {
          fetchData();
        });
      } catch (err) {
        console.error(`[onMounted 错误] ${err.message}`);
      }
    });

    onBeforeUnmount(() => {
      try {
        console.log('组件卸载，清理资源');
        // 销毁图表实例
        if (incomeChartInstance) {
          incomeChartInstance.dispose();
          incomeChartInstance = null; // 明确置空
          window.removeEventListener('resize', () => incomeChartInstance?.resize())
        }
        if (pcfChartInstance) {
          pcfChartInstance.dispose();
          pcfChartInstance = null; // 明确置空
          window.removeEventListener('resize', () => pcfChartInstance?.resize())
        }
        if (profitChartInstance) {
          profitChartInstance.dispose();
          profitChartInstance = null; // 明确置空
          window.removeEventListener('resize', () => profitChartInstance?.resize())
        }

      } catch (err) {
        console.error(`[onBeforeUnmount 错误] ${err.message}`);
      }
    });

    // 数据库字段与注释的映射关系
    const fieldComments = {
      company_basic_info: {
        id: '自增主键',
        key_no: '企业唯一标识',
        name: '企业名称',
        no: '注册号/编号',
        belong_org: '登记机关',
        oper_id: '法定代表人ID',
        oper_name: '法定代表人名称',
        start_date: '成立日期',
        end_date: '吊销日期',
        status: '登记状态',
        province: '省份代码',
        updated_date: '更新日期',
        credit_code: '统一社会信用代码',
        regist_capi: '注册资本(旧字段)',
        registered_capital: '注册资本数额',
        registered_capital_unit: '注册资本单位',
        registered_capital_ccy: '注册资本币种',
        econ_kind: '企业类型',
        address: '注册地址',
        scope: '经营范围',
        term_start: '营业期限始',
        term_end: '营业期限至',
        check_date: '核准日期',
        org_no: '组织机构代码',
        is_on_stock: '是否上市(0-未上市,1-上市)',
        stock_number: '股票代码',
        stock_type: '上市类型',
        image_url: '企业Logo地址',
        ent_type: '企业性质',
        rec_cap: '实缴资本(旧字段)',
        paid_up_capital: '实缴出资额数额',
        paid_up_capital_unit: '实缴出资额单位',
        paid_up_capital_ccy: '实缴出资额币种',
        area_code: '行政区划代码',
        create_time: '创建时间',
        update_time: '更新时间'
      },
      before_invest_income_statement: {
        store_ent_code: '门店编码',
        period: '月份',
        operating_income: '营业收入',
        other_operating_income: '营业收入-其他业务收入',
        operating_cost: '营业成本',
        supermarket_cost: '营业成本-超市',
        snack_cost: '营业成本-小吃',
        bar_cost: '营业成本-水吧',
        beverage_cost: '营业成本-调饮',
        fixed_costs: '固定费用合计',
        copyright_fee: '固定费用-版权费',
        song_addition_fee: '固定费用-加歌费',
        sales_tax: '固定费用-销售税金',
        dormitory_rent: '固定费用-宿舍房租',
        store_rent: '固定费用-店面租金',
        store_property_fee: '固定费用-店面物业费',
        semi_fixed_costs: '半固定费用合计',
        salary: '半固定费用-工资',
        water_fee: '半固定费用-水费',
        electricity_fee: '半固定费用-电费',
        communication_fee: '半固定费用-通讯费',
        staff_meal: '半固定费用-员工餐',
        bonus: '半固定费用-提成奖金',
        social_insurance: '半固定费用-社会保险费',
        management_fee: '半固定费用-上缴管理费',
        variable_costs: '变动费用合计',
        travel_expense: '变动费用-差旅费',
        maintenance_fee: '变动费用-维修费',
        work_clothing_fee: '变动费用-工装费',
        office_expense: '变动费用-办公费',
        welfare_fee: '变动费用-福利费',
        pest_control_fee: '变动费用-灭虫费',
        dormitory_expense: '变动费用-宿舍费用',
        other_expense: '变动费用-其他费用',
        fire_control_fee: '变动费用-消防费用',
        car_expense: '变动费用-汽车费用',
        insurance_fee: '变动费用-财险/人险',
        business_entertainment: '变动费用-业务招待费',
        card_handling_fee: '变动费用-刷卡手续费',
        group_purchase_fee: '变动费用-团购手续费',
        interest_income: '变动费用-利息收入',
        advertising_fee: '变动费用-广告宣传费',
        low_value_consumables: '变动费用-低值易耗品',
        store_decoration: '变动费用-店面装修、设备更换',
        recharge_gift: '变动费用-充值赠送',
        depreciation_fee: '变动费用-折旧费',
        voucher: '变动费用-抵用券',
        total_costs: '费用合计',
        net_profit: '净利润',
        gross_profit: '毛利润',
        net_profit_margin: '净利润率',
        created_at: '创建时间',
        update_at: '更新时间',
        guaranteed_revenue: '保本营业额'
      }
    };

    // 驼峰转下划线函数
    const camelToSnakeCase = (str) => {
      return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    };

    // 获取字段注释
    const getFieldComment = (tableName, fieldName) => {
      try {
        // 将驼峰格式字段转换为下划线格式
        const snakeCaseField = camelToSnakeCase(fieldName);

        // 如果有对应的注释则返回注释，否则返回格式化后的字段名
        if (fieldComments[tableName] && fieldComments[tableName][snakeCaseField]) {
          return fieldComments[tableName][snakeCaseField];
        }

        // 未找到注释时的降级处理
        return fieldName.replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .replace(/_/g, ' ');
      } catch (err) {
        console.error(`[getFieldComment 错误] ${err.message}`, { tableName, fieldName });
        return fieldName;
      }
    };

    // 格式化数值
    const formatValue = (value) => {
      try {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') {
          return value % 1 === 0 ? value.toLocaleString() : value.toFixed(2);
        }
        return value;
      } catch (err) {
        console.error(`[formatValue 错误] ${err.message}`, { value });
        return '-'; // 出错时返回占位符
      }
    };

    // 格式化货币
    const formatCurrency = (value) => {
      try {
        if (value === null || value === undefined) return '-';
        return  Number(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
      } catch (err) {
        console.error(`[formatCurrency 错误] ${err.message}`, { value });
        return '-'; // 出错时返回占位符
      }
    };

    // 1. 提取公共方法获取保本营业额文本
    const getGuaranteedRevenueText = (data) => {
      const hasValue = data.value?.guaranteed_revenue !== undefined
          && data.value?.guaranteed_revenue !== null
          && data.value?.guaranteed_revenue !== '';

      if (!hasValue) {
        return '';
      }

      const revenueValue = Number(data.value.guaranteed_revenue);
      if (isNaN(revenueValue)) {
        return '';
      }

      // 格式化金额为千分位
      const formattedValue = revenueValue.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return `（${getFieldComment('before_invest_income_statement', 'guaranteed_revenue')}: ¥${formattedValue}）`;
    };


    const getIncomeStatementValue = (field, period) => {
      const list = data.value.BeforeInvestIncomeStatementList;
      if (!list) return '-';
      const row = list.find(item => item.period === period);
      if (!row) return '-';
      if (field === 'grossProfit') {
        const inc = Number(row.operatingIncome || 0);
        const cost = Number(row.operatingCost || 0);
        return inc - cost;
      }
      return row[field];
    };

    const getAvg12Value = (field) => {
      const periods = Array.isArray(last12Periods.value) ? last12Periods.value : [];
      const list = Array.isArray(data.value?.BeforeInvestIncomeStatementList)
        ? data.value.BeforeInvestIncomeStatementList
        : [];
      if (periods.length === 0 || list.length === 0) return '-';
      const rows = list.filter(r => periods.includes(r.period));
      if (!rows || rows.length === 0) return '-';
      const safeAvg = (sum, count) => count > 0 ? sum / count : 0;
      if (field === 'grossProfit') {
        const sum = rows.reduce((s, r) => s + ((Number(r.operatingIncome) || 0) - (Number(r.operatingCost) || 0)), 0);
        return safeAvg(sum, rows.length);
      }
      const sum = rows.reduce((s, r) => s + (Number(r[field]) || 0), 0);
      return safeAvg(sum, rows.length);
    };
    const investorShareRatioPct = computed(() => Number(proxy?.revenueSharingRatio) || 0);
    const controllerCashFlowRows = computed(() => {
      if (!data.value.totalNet_profit) return [];
      const monthTurnoverMap = new Map();
      const pcfList = Array.isArray(data.value.beforeInvestStoreIncomeDetailPcfList) ? data.value.beforeInvestStoreIncomeDetailPcfList : [];
      pcfList.forEach(row => {
        const d = new Date(row.incomeDate);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const val = Number(row.turnover) || 0;
        monthTurnoverMap.set(key, (monthTurnoverMap.get(key) || 0) + val);
      });
      const entries = Object.entries(data.value.totalNet_profit).sort((a, b) => new Date(a[0]) - new Date(b[0]));
      return entries.map(([k, v]) => {
        const d = new Date(k);
        const label = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const net = Number(v) || 0;
        const monthTurnover = monthTurnoverMap.get(label) || 0;
        const investor = monthTurnover * (investorShareRatioPct.value / 100);
        const ctrl = net - investor;
        return { label, turnover: monthTurnover, net, ratio: investorShareRatioPct.value, investor, ctrl };
      });
    });

    const avg12Net = computed(() => {
      const arr = controllerCashFlowRows.value.slice(Math.max(0, controllerCashFlowRows.value.length - 12));
      if (arr.length === 0) return 0;
      return arr.reduce((s, r) => s + r.net, 0) / arr.length;
    });
    const avg12Turnover = computed(() => {
      const arr = controllerCashFlowRows.value.slice(Math.max(0, controllerCashFlowRows.value.length - 12));
      if (arr.length === 0) return 0;
      return arr.reduce((s, r) => s + r.turnover, 0) / arr.length;
    });
    const avg12Investor = computed(() => {
      const arr = controllerCashFlowRows.value.slice(Math.max(0, controllerCashFlowRows.value.length - 12));
      if (arr.length === 0) return 0;
      return arr.reduce((s, r) => s + r.investor, 0) / arr.length;
    });
    const avg12Ctrl = computed(() => {
      const arr = controllerCashFlowRows.value.slice(Math.max(0, controllerCashFlowRows.value.length - 12));
      if (arr.length === 0) return 0;
      return arr.reduce((s, r) => s + r.ctrl, 0) / arr.length;
    });

    const renderCashFlowChart = () => {
      if (!cashFlowChart.value || controllerCashFlowRows.value.length === 0) return;
      if (cashChartInstance) cashChartInstance.dispose();
      cashChartInstance = echarts.init(cashFlowChart.value);
      const labels = controllerCashFlowRows.value.map(r => r.label);
      const values = controllerCashFlowRows.value.map(r => ({ value: r.ctrl, itemStyle: { color: r.ctrl < 0 ? '#f56c6c' : '#36a2eb' } }));
      const option = {
        title: { text: '实控人现金流（按月）', left: 'center' },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: labels },
        yAxis: { type: 'value', axisLabel: { formatter: (v) => `${Number(v).toLocaleString()}` } },
        series: [{ name: '现金流', type: 'line', data: values, smooth: true, symbol: 'circle', symbolSize: 4 }]
      };
      cashChartInstance.setOption(option);
      window.addEventListener('resize', () => cashChartInstance?.resize());
    };

    watch([investorShareRatioPct, () => data.value.totalNet_profit], () => {
      nextTick(() => renderCashFlowChart());
    });

    watch(() => proxy && proxy.revenueSharingRatio, () => {
      nextTick(() => renderCashFlowChart());
    });
    watch(() => data.value.beforeInvestStoreIncomeDetailPcfList, () => {
      nextTick(() => renderCashFlowChart());
    });

    // 营业额预估方法
    const forecastTurnover = async () => {
      forecastLoading.value = true;
      forecastMessage.value = '';
      forecastSuccess.value = false;
      
      try {
        const result = await assetService.forecastTurnover(props.storeEntCode);
        
        if (result.code === 200) {
          forecastSuccess.value = true;
          forecastMessage.value = '营业额预估成功！数据已保存到数据库。';
          // 刷新页面数据
          await fetchData();
        } else {
          forecastSuccess.value = false;
          forecastMessage.value = `预估失败：${result.msg || '未知错误'}`;
        }
      } catch (err) {
        forecastSuccess.value = false;
        forecastMessage.value = `预估失败：${err.message}`;
      } finally {
        forecastLoading.value = false;
        
        // 3秒后清除消息
        setTimeout(() => {
          forecastMessage.value = '';
        }, 3000);
      }
    };

    return {
      loading,
      error,
      data,
      equityRatio,
      totalEquityProfit,
      pcfAdjustmentRatio,
      forecastLoading,
      forecastMessage,
      forecastSuccess,
      incomeDetailChart,
      pcfChart,
      netProfitChart,
      cashFlowChart,
      incomeStatementFields,
      incomeStatementPeriods,
      last12Periods,
      getFieldComment,
      formatValue,
      formatCurrency,
      calculateEquityProfit,
      recalculateNetProfit,
      renderPcfChart,
      filteredCompanyBasicInfo,
      calculatePcfTotal,
      getGuaranteedRevenue,
      last3MonthsTotal,
      pcfFirst3MonthsTotal,
      difference,
      ratio,
      getIncomeStatementValue,
      getAvg12Value,
      investorShareRatioPct,
      controllerCashFlowRows,
      avg12Net,
      avg12Investor,
      avg12Ctrl,
      forecastTurnover
    };
  }
};
</script>

<style scoped>
/* 在样式表中添加基础样式优化 */
.container {
  /* 增加页面最大宽度限制，避免过宽屏幕显示问题 */
  max-width: 1600px;
  /* 优化页面内边距 */
  padding: 2rem 1rem;
}

/* 卡片容器统一美化 */
section {
  /* 添加卡片阴影层次感 */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  /* 圆角优化 */
  border-radius: 12px;
  /* 增加内边距呼吸感 */
  padding: 1.5rem;
  /* 卡片间间距 */
  margin-bottom: 2rem !important;
  /* 平滑过渡效果 */
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

section:hover {
  /* 悬停效果增强 */
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

/* 区块标题样式 */
h2 {
  color: #2d3748;
  font-weight: 600;
  margin-bottom: 1rem !important;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

/* 子标题样式 */
h3 {
  color: #2c5282;
  font-weight: 600;
}

.spinner-border {
  border-top-color: #3e95cd;
  border-right-color: #ff6384;
  border-bottom-color: #4bc0c0;
  border-left-color: #ffce56;
}

/* 表格整体样式 */
table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

/* 表头样式 */
thead {
  background-color: #f8fafc;
}

/* 表格内容样式 */
td {
  padding: 0.75rem 1rem !important;
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.1s ease;
}

th {
  color: #4a5568;
  font-weight: 600;
  padding: 0.75rem 1rem !important;
  text-align: left;
  border-bottom: 2px solid #e2e8f0;
}

tbody tr:hover td {
  background-color: #f8fafc;
}

/* 滚动表格容器美化 */
.scrollable-table,
section > div[style*="overflow-x: auto"] {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 0 !important;
  overflow: auto;
}

/* 表格内固定列样式优化 */
td[style*="position: sticky"],
th[style*="position: sticky"] {
  background-color: white;
  z-index: 20;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.03);
}

/* 图表容器样式增强 */
.chart-wrapper {
  width: 100%;
  min-width: 400px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 300px;
}

/* 滚动表格样式增强 */
.scrollable-table {
  -webkit-overflow-scrolling: touch; /* 平滑滚动 */
  scrollbar-width: thin; /* 细滚动条 */
}

.scrollable-table::-webkit-scrollbar {
  height: 6px;
}

.scrollable-table::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

/* 确保表格内容不换行 */
.whitespace-nowrap {
  white-space: nowrap !important;
}

input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

.flex {
  display: inline-block;
  vertical-align: middle;
  margin-right: 10px; /* 可以根据需要调整间距 */
}

/* 输入框样式 */
input[type="number"] {
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 0.3rem 0.3rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

input[type="number"]:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

/* 加载动画美化 */
.spinner-border {
  width: 3rem !important;
  height: 3rem !important;
  border-width: 4px !important;
  border-color: #e2e8f0 #94a3b8 #64748b #3b82f6 !important;
}

/* 加载区域样式 */
div[class*="flex justify-center items-center h-64"] {
  background-color: #f8fafc;
  border-radius: 12px;
}


</style>

